import { Toggle } from "@base-ui/react/toggle";
import { ToggleGroup } from "@base-ui/react/toggle-group";
import type { FC } from "react";
import React from "react";
import {
  ComputerIcon,
  MoonIcon,
  SunIcon,
} from "@/components/ui/icons/ThemeIcons";
import { ColorScheme, useTheme } from "@/context/ThemeContext";
import { useThemePreview } from "@/hooks/useThemePreview";
import type { BaseComponentProps } from "@/types/component-props";
import { cn } from "@/utils/cn";
import { themeComponentStyles } from "@/utils/themeUtils";

export interface SchemeToggleGroupProps extends BaseComponentProps {
  /** Whether to show labels next to icons (default: false) */
  showLabels?: boolean;
  /** Delay in ms before showing color scheme preview when hovering (default: 450ms) */
  previewDelay?: number;
  /** Whether to disable the preview functionality (default: false) */
  disablePreview?: boolean;
}

/**
 * ColorSchemeSelector
 *
 * A component for switching between light, dark, and system color schemes (light/dark mode).
 * This component controls the color scheme (light/dark), NOT the theme colors/palette.
 * Uses Base UI's ToggleGroup component for accessibility and state management.
 */
export const SchemeToggleGroup: FC<SchemeToggleGroupProps> = ({
  className,
  showLabels = false,
  previewDelay = 450,
  disablePreview = false,
}) => {
  const { colorScheme, setColorScheme, previewColorScheme } = useTheme();

  // Use theme preview hook to manage preview behavior
  const { handleMouseEnter: baseHandleMouseEnter, handleMouseLeave } =
    useThemePreview(previewColorScheme, {
      delay: previewDelay,
      disabled: disablePreview,
    });

  // Handle value change
  const handleValueChange = (value: ColorScheme[]) => {
    const [nextColorScheme] = value;

    if (nextColorScheme) {
      setColorScheme(nextColorScheme);
    }
  };

  // Extended version of handleMouseEnter that skips if it's the active scheme
  const handleMouseEnter = React.useCallback(
    (scheme: ColorScheme) => {
      // Skip if it's already the active scheme
      if (scheme === colorScheme) return;

      // Otherwise use the generic preview handler
      baseHandleMouseEnter(scheme);
    },
    [colorScheme, baseHandleMouseEnter],
  );

  return (
    <ToggleGroup
      aria-label="Select color scheme"
      className={cn(
        "inline-flex items-center justify-center p-0.5",
        themeComponentStyles.getContainerClasses({
          isSchemeToggleGroup: true,
        }),
        className,
      )}
      onValueChange={handleValueChange}
      value={[colorScheme]}
    >
      <Toggle
        aria-label="Light mode"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "data-pressed:bg-white dark:data-pressed:bg-gray-900 data-pressed:text-theme-primary dark:data-pressed:text-theme-primary data-pressed:shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:data-pressed:shadow-[0_1px_3px_0_rgba(0,0,0,0.4)] font-medium",
          "text-theme-muted-foreground",
          "hover:bg-theme-muted/70",
        )}
        onMouseEnter={() => handleMouseEnter(ColorScheme.LIGHT)}
        onMouseLeave={handleMouseLeave}
        value={ColorScheme.LIGHT}
      >
        <SunIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>Light</span>}
      </Toggle>
      <Toggle
        aria-label="System preference"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "data-pressed:bg-white dark:data-pressed:bg-gray-900 data-pressed:text-theme-primary dark:data-pressed:text-theme-primary data-pressed:shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:data-pressed:shadow-[0_1px_3px_0_rgba(0,0,0,0.4)] font-medium",
          "text-theme-muted-foreground",
          "hover:bg-theme-muted/70",
        )}
        onMouseEnter={() => handleMouseEnter(ColorScheme.SYSTEM)}
        onMouseLeave={handleMouseLeave}
        value={ColorScheme.SYSTEM}
      >
        <ComputerIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>System</span>}
      </Toggle>
      <Toggle
        aria-label="Dark mode"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "data-pressed:bg-white dark:data-pressed:bg-gray-900 data-pressed:text-theme-primary dark:data-pressed:text-theme-primary data-pressed:shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:data-pressed:shadow-[0_1px_3px_0_rgba(0,0,0,0.4)] font-medium",
          "text-theme-muted-foreground",
          "hover:bg-theme-muted/70",
        )}
        onMouseEnter={() => handleMouseEnter(ColorScheme.DARK)}
        onMouseLeave={handleMouseLeave}
        value={ColorScheme.DARK}
      >
        <MoonIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>Dark</span>}
      </Toggle>
    </ToggleGroup>
  );
};
